'use client';
import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import SectionHeader from '@/components/ui/SectionHeader';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  quote: string;
  name: string;
  title: string;
  company: string;
  avatar: string;
  category: 'Collaborator' | 'Client' | 'Founder' | 'Operator' | 'Business Owner';
  traits: string[];
  isPlaceholder?: boolean;
}

export default function TestimonialsPage() {
  const categories = ['All', 'Client', 'Founder', 'Operator', 'Collaborator', 'Business Owner'];
  const traits = ['All', 'Clarity', 'Speed', 'Execution', 'Judgment', 'Follow-Through', 'Problem Solving', 'Strategic Thinking'];

  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedTrait, setSelectedTrait] = useState<string>('All');

  const testimonials: Testimonial[] = [
    {
      id: 'testimonial-7',
      quote: 'From the first day I met Daniel, I was impressed with his innate ability to analyze a situation and quickly assess the optimal solution. Not one to just accept the status quo, Daniel will educate himself on a topic and delve into the problems to truly understand the issue and create a unique and successful solution.',
      name: 'Craig Chilcote',
      title: 'Managing Partner',
      company: 'Anderson, Kuiti & Asuma P.L.L.C.',
      avatar: '/assets/images/craig-chilcote-180x180-1771401822004.jpg',
      category: 'Business Owner',
      traits: ['Judgment', 'Problem Solving', 'Clarity']
    },
    {
      id: 'testimonial-9',
      quote: 'I\'ve known Dan since he was a young hotshot starting his first company. He\'s one of the most driven producers of results I\'ve dealt with in the Internet marketing industry. His executive skills are impressive. I mentored him for a while when he got started, but now, if I need advice about managing a company, Dan is on my short list of people to call.',
      name: 'Randy Ray',
      title: 'CEO',
      company: 'Reflective Dynamics',
      avatar: '/assets/images/randy-ray-portrait-02-1771402065850.jpg',
      category: 'Founder',
      traits: ['Judgment', 'Strategic Thinking', 'Execution']
    },
    {
      id: 'testimonial-10',
      quote: 'Daniel is honestly one of the most intelligent, driven, and hardest working individuals I\'ve met in the digital media business. This industry is filled with highly driven entrepreneurs and skilled professionals, yet Daniel consistently distinguishes himself with creativity and uncommon dedication to his work.',
      name: 'J. Todd',
      title: 'Executive Director',
      company: 'This Week in Digital Media',
      avatar: '/assets/images/j-todd-180x180-1771402130351.jpg',
      category: 'Collaborator',
      traits: ['Execution', 'Strategic Thinking', 'Follow-Through']
    },
    {
      id: 'testimonial-6',
      quote: 'Daniel is one of the most savvy and creative SEO and online marketers I have come across in my career. He has a firm grasp of all online marketing activities designed to increase website traffic, improve conversions, and expand brand recognition across the Web. He is consistently developing new ways to reach customers using the latest Internet technologies. On top of his technical and creative expertise, Dan upholds the highest levels of integrity.',
      name: 'Mark Subel',
      title: 'Digital Marketing Consultant',
      company: 'Focal Click',
      avatar: '/assets/images/mark-subel-180x180-1771401981332.jpg',
      category: 'Collaborator',
      traits: ['Strategic Thinking', 'Execution', 'Problem Solving']
    },
    {
      id: 'testimonial-1',
      quote: 'Daniel was probably one of the most successful performance marketers I have ever worked with, and I have worked with +20,000 of them. Daniel quickly identified a niche in a very competitive marketplace and became the best in that niche — a win-win situation for us both.',
      name: 'Edward Ihre',
      title: 'Owner & CEO',
      company: 'Ihre Consulting',
      avatar: '/assets/images/image-1771401264226.png',
      category: 'Business Owner',
      traits: ['Execution', 'Strategic Thinking', 'Problem Solving']
    },
    {
      id: 'testimonial-8',
      quote: 'Daniel is great to work with — a savvy Internet professional who delivers results. He has a great knowledge base and is always watching out for smart opportunities and bringing ideas above and beyond our regular campaign. I have no hesitation in recommending him.',
      name: 'Rohit Prathinidhi',
      title: 'Partner Program Manager',
      company: 'PartyGaming',
      avatar: '',
      category: 'Client',
      traits: ['Execution', 'Follow-Through', 'Speed']
    },
    {
      id: 'testimonial-2',
      quote: 'Working with Dan and the BOC team was a great experience. I was more than impressed with the integrity of their work and the success of their marketing campaigns. I highly recommend Best Odds Corp to anyone looking for comprehensive online advertising solutions.',
      name: 'Rick Peterson',
      title: 'Partner Manager',
      company: 'Affactive',
      avatar: '',
      category: 'Collaborator',
      traits: ['Execution', 'Follow-Through']
    },
    {
      id: 'testimonial-3',
      quote: 'Daniel has been producing phenomenal niche media websites for a long time, with some of the best niche content available online. He has grown his portfolio of sites and positioned himself as one of the leading performance consultants in the game.',
      name: 'Tom Galanis',
      title: 'Director',
      company: 'GameOn',
      avatar: '',
      category: 'Collaborator',
      traits: ['Strategic Thinking', 'Execution']
    },
    {
      id: 'testimonial-4',
      quote: 'Thanks to their extensive knowledge of SEO, exquisite lateral thinking, and innovative promotional ideas, the professional and friendly team of Best Odds Corp is a great partner to collaborate with.',
      name: 'Switch Affiliate Team',
      title: 'Partner Team',
      company: 'Switch Partner Team',
      avatar: '',
      category: 'Collaborator',
      traits: ['Strategic Thinking', 'Problem Solving']
    },
    {
      id: 'testimonial-5',
      quote: 'It\'s been a pleasure collaborating with Seb and Daniel from Best Odds Corp. They are very prompt in responding to any questions and have always worked well with us in achieving the same goals. Seb and Daniel are all around great guys so it makes doing business with them an enjoyable experience.',
      name: 'Dione Scott-Rogers',
      title: 'Partner Manager',
      company: 'PKR',
      avatar: '/assets/images/dione-scott-rogers-pkr-1771401846346.jpg',
      category: 'Collaborator',
      traits: ['Follow-Through', 'Execution']
    }
  ];

  const filteredTestimonials = testimonials.filter(t => {
    const categoryMatch = selectedCategory === 'All' || t.category === selectedCategory;
    const traitMatch = selectedTrait === 'All' || t.traits.includes(selectedTrait);
    return categoryMatch && traitMatch;
  });

  const traitDescriptions: Record<string, string> = {
    Clarity: 'Simplifies complex problems. Communicates what matters.',
    Speed: 'Moves fast. Ships without unnecessary delays.',
    Execution: 'Does the work. Delivers consistently.',
    Judgment: 'Makes the right call. Reads situations accurately.',
    'Follow-Through': 'Finishes what\'s started. Accountable to outcomes.',
    'Problem Solving': 'Finds the real issue. Builds the right fix.',
    'Strategic Thinking': 'Sees the bigger picture. Connects dots others miss.'
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        {/* Hero Section */}
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <SectionHeader
                eyebrow="What People Say"
                title="Real Feedback. Real People."
                description="These are real testimonials from founders, operators, and collaborators who have worked with Daniel and Best Odds Corp. No invented quotes. No stock photos. No fabricated outcomes."
                align="center"
              />
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <Badge variant="default">Real Testimonials Only</Badge>
                <Badge variant="default">5–10 Fresh Testimonials Coming Soon</Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Trait Legend */}
        <section className="py-12 px-6 border-b border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <p className="text-micro uppercase tracking-ultra-wide text-muted-foreground text-center mb-6">What We're Known For</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {Object.entries(traitDescriptions).map(([trait, desc]) => (
                <button
                  key={trait}
                  onClick={() => setSelectedTrait(selectedTrait === trait ? 'All' : trait)}
                  className={`p-4 border text-left transition-all ${
                    selectedTrait === trait
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary'
                  }`}
                >
                  <span className="text-micro uppercase tracking-widest text-primary block mb-1">{trait}</span>
                  <span className="text-xs text-muted-foreground leading-tight">{desc}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-10 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-start md:items-center justify-between">
              {/* Category Filter */}
              <div>
                <p className="text-micro uppercase tracking-ultra-wide text-muted-foreground mb-3">Filter by Category</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-4 py-2 text-micro uppercase tracking-widest border transition-all ${
                        selectedCategory === cat
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reset + Count */}
              <div className="flex items-center gap-4">
                {(selectedCategory !== 'All' || selectedTrait !== 'All') && (
                  <button
                    onClick={() => { setSelectedCategory('All'); setSelectedTrait('All'); }}
                    className="px-4 py-2 border border-border hover:border-primary text-micro uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Reset
                  </button>
                )}
                <span className="text-body-sm text-muted-foreground">
                  {filteredTestimonials.length} of {testimonials.length} shown
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Grid */}
        <section className="py-24 px-6">
          <div className="max-w-[1600px] mx-auto">
            {filteredTestimonials.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-h4 text-muted-foreground mb-4">No testimonials match these filters</p>
                <button
                  onClick={() => { setSelectedCategory('All'); setSelectedTrait('All'); }}
                  className="px-6 py-3 border border-border hover:border-primary text-label uppercase tracking-widest text-foreground transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTestimonials.map(t => (
                  <div key={t.id} className="border border-border hover:border-primary transition-all duration-300 bg-card flex flex-col">
                    <div className="p-8 flex-1">
                      {/* Category badge */}
                      <div className="flex items-center justify-between mb-6">
                        <Badge variant="default">{t.category}</Badge>
                        <Icon name="ChatBubbleLeftIcon" size={20} className="text-primary opacity-40" />
                      </div>
                      {/* Quote */}
                      <blockquote className="text-body-sm text-foreground leading-relaxed mb-6">
                        &ldquo;{t.quote}&rdquo;
                      </blockquote>
                      {/* Traits */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {t.traits.map(trait => (
                          <span key={trait} className="px-2 py-1 text-xs border border-border text-muted-foreground uppercase tracking-widest">
                            {trait}
                          </span>
                        ))}
                      </div>
                    </div>
                    {/* Attribution */}
                    <div className="px-8 py-6 border-t border-border flex items-center gap-4">
                      {t.avatar ? (
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-border flex-shrink-0">
                          <AppImage
                            src={t.avatar}
                            alt={`${t.name} - ${t.title}`}
                            width={40}
                            height={40}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full border border-border bg-surface-2 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">{t.name.charAt(0)}</span>
                        </div>
                      )}
                      <div>
                        <p className="text-body-sm font-semibold text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{t.title}{t.company ? `, ${t.company}` : ''}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Placeholder Section — Future Testimonials */}
        <section className="py-24 px-6 border-t border-border bg-surface-1">
          <div className="max-w-[1600px] mx-auto">
            <div className="reveal mb-12">
              <SectionHeader
                eyebrow="Coming Soon"
                title="5–10 Fresh Testimonials In Progress"
                description="We are actively collecting current testimonials from founders, operators, and collaborators who have worked with Daniel on sprint-based, AI-driven, and execution-focused engagements. These will be added as they are received."
                align="center"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
              {categories.filter(c => c !== 'All').map((cat) => (
                <div key={cat} className="p-6 border border-dashed border-border bg-card text-center">
                  <div className="w-10 h-10 rounded-full border border-dashed border-border mx-auto mb-4 flex items-center justify-center">
                    <Icon name="UserIcon" size={18} className="text-muted-foreground" />
                  </div>
                  <p className="text-micro uppercase tracking-ultra-wide text-primary mb-1">{cat}</p>
                  <p className="text-xs text-muted-foreground">Slot reserved</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-body-sm text-muted-foreground max-w-xl mx-auto">
                If you have worked with Daniel or Best Odds Corp and would like to share your experience, reach out via the contact page.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-6 border-t border-border">
          <div className="max-w-[1600px] mx-auto text-center">
            <h2 className="text-h2 mb-6">Ready to work with an operator who executes?</h2>
            <p className="text-body text-muted-foreground mb-12 max-w-2xl mx-auto">
              Start with a Sprint — a paid diagnostic that maps your constraints and delivers a clear execution roadmap. No pressure to commit to a retainer before we understand your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/book">
                <Button variant="primary" size="lg" className="w-full sm:w-auto">
                  Book a Sprint — Start With a Diagnostic
                </Button>
              </Link>
              <Link href="/case-studies">
                <Button variant="secondary" size="lg" className="w-full sm:w-auto">
                  View Proof & Process
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}